/**
 * Spec 200 Fase 5 #5 — assign / unassign technicians.
 *
 * Read-mode: avatar-style chips with the worker's initials + name.
 * Empty: a placeholder pill that opens the dropdown.
 *
 * Click any chip → opens a dropdown (popover under the row) with
 * every team worker as a checkbox. Toggling a checkbox calls
 * `onChange(nextIds)`; the parent broadcasts via `patchWork`.
 *
 * No fancy combobox: native `<details>`/`<summary>` would be
 * accessible but doesn't compose with the click-anywhere-to-close
 * behaviour we want, so we own a tiny `open` state.
 */
import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';

import type { WorkerListEntry } from '../sync/relay';

import './TechnicianMultiSelect.css';

interface TechnicianMultiSelectProps {
  /** Currently-assigned technician ids. */
  value: string[];
  /** All workers known to the company (from `useWorkers`). */
  workers: WorkerListEntry[];
  onChange: (next: string[]) => void | Promise<void>;
  testId?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Map a worker name to one of the avatar tokens (1..6) so a card
 *  with five teammates renders five distinct circles instead of
 *  five identical navy ones. The hash is deterministic per name —
 *  the same person gets the same colour every render. (Slack/Notion
 *  pattern.) */
function avatarColorIndex(name: string): 1 | 2 | 3 | 4 | 5 | 6 {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return ((Math.abs(h) % 6) + 1) as 1 | 2 | 3 | 4 | 5 | 6;
}

export function TechnicianMultiSelect({
  value,
  workers,
  onChange,
  testId,
}: TechnicianMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(id: string): void {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    void onChange(next);
  }

  // Map id → workers entry for the chip rendering. Some ids may not
  // be in the roster yet (the worker hasn't connected since we got
  // the list); render those as `Worker XXXXXX` so the user can still
  // see them and unassign.
  const byId = new Map(workers.map((w) => [w.id, w]));
  const assigned = value.map((id) => ({
    id,
    name: byId.get(id)?.name ?? `Worker ${id.slice(0, 6)}`,
  }));

  return (
    <div className="tech-select" data-testid={testId} ref={containerRef}>
      <div className="tech-chips">
        {assigned.map((a) => (
          <button
            key={a.id}
            type="button"
            className="tech-chip"
            onClick={() => setOpen((o) => !o)}
            data-testid={testId ? `${testId}-chip-${a.id}` : undefined}
            aria-label={`Editar técnicos asignados — ${a.name}`}
          >
            <span
              className={`tech-avatar tech-avatar--c${avatarColorIndex(a.name)}`}
              aria-hidden="true"
            >
              {initials(a.name)}
            </span>
            {a.name}
          </button>
        ))}
        <button
          type="button"
          className="tech-add"
          onClick={() => setOpen((o) => !o)}
          data-testid={testId ? `${testId}-add` : undefined}
          aria-label="Asignar técnicos"
          aria-expanded={open}
        >
          {assigned.length === 0 ? (
            <>
              <Plus size={16} strokeWidth={2} aria-hidden="true" />
              Asignar
            </>
          ) : (
            <>
              <Plus size={16} strokeWidth={2} aria-hidden="true" />
              Más
            </>
          )}
        </button>
      </div>

      {open && (
        <div className="tech-dropdown" role="listbox" data-testid={testId ? `${testId}-dropdown` : undefined}>
          <div className="tech-dropdown-header">
            <span>Selecciona técnicos</span>
            <button
              type="button"
              className="tech-close"
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          {workers.length === 0 ? (
            <p className="tech-empty">Aún no hay miembros conectados.</p>
          ) : (
            <ul className="tech-options">
              {workers.map((w) => {
                const checked = value.includes(w.id);
                return (
                  <li key={w.id}>
                    <label
                      className="tech-option"
                      data-testid={testId ? `${testId}-option-${w.id}` : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(w.id)}
                      />
                      <span
                        className={`tech-avatar tech-avatar--small tech-avatar--c${avatarColorIndex(w.name)}`}
                        aria-hidden="true"
                      >
                        {initials(w.name)}
                      </span>
                      <span className="tech-option-name">{w.name}</span>
                      {w.role && w.role !== 'member' && (
                        <span className="tech-option-role">{w.role}</span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
